
import {
  Vue, Component, Prop, Watch,
} from 'vue-property-decorator';
import UserGroups from '@/components/UserGroups.vue';
import {
  AssignmentType, Lead, PermissionCode, User,
} from '@/types';

@Component({
  components: {
    UserGroups,
  },
})
export default class EditLeadUsersDialog extends Vue {
  @Prop(Boolean) readonly value!: boolean;
  @Prop(Number) readonly projectId!: number;

  dialog = false
  valid = false
  loading = false

  update = {
    requester: null,
    owners: [],
  } as {
    requester?: null | number;
    owners: number[];
  }

  @Watch('value')
  async onValueChange(val: boolean) {
    if (val === true) {
      // Serve la lista dei tipi incarico per ricavare i prefissi delle CV
      // `${prefix}Assignees` usate da lockedOwnerIds (fallback già presente).
      if (this.$store.getters.getAssignmentTypes.length === 0) {
        this.$store.dispatch('fetchAssignmentTypes');
      }

      if (this.projectId) {
        await this.$store.dispatch('selectLead', { id: this.projectId });
      }

      this.$set(this.update, 'requester', this.lead.requesterId);
      this.$set(this.update, 'owners', this.leadOwners);
    }
    this.dialog = val;
  }

  @Watch('dialog')
  onDialogChange(val: boolean) {
    if (this.value !== val) {
      this.$emit('input', val);
    }
  }

  // Catch-all: gli owner bloccati (assegnatari incarico) non sono rimovibili da
  // qui per nessuna via (chip close, click nel dropdown, backspace). Se uno
  // viene tolto, lo re-inseriamo. Il chip mostra anche un lucchetto e niente
  // pulsante di chiusura come segnale visivo.
  @Watch('update.owners')
  onUpdateOwnersChange(val: number[]) {
    const locked = this.lockedOwnerIds;
    if (locked.size === 0) return;
    const current = Array.isArray(val) ? val : [];
    const missing = Array.from(locked).filter((id) => !current.includes(id));
    if (missing.length > 0) {
      this.$set(this.update, 'owners', [...current, ...missing]);
    }
  }

  get user(): User {
    return this.$store.getters.currentUser;
  }

  get lead(): Lead {
    return this.$store.getters.currentLead;
  }

  get users() {
    return this.$store.getters.users;
  }

  get userPermissions() {
    return this.$store.getters.currentUserPermissions;
  }

  get hasRequester() {
    return this.lead && 'requesterId' in this.lead && this.lead.requesterId;
  }

  get hasOwners() {
    return this.lead && Array.isArray(this.lead.owners) && this.lead.owners.length > 0;
  }

  get leadOwners() {
    return this.hasOwners
      ? this.lead.owners.map((u: User) => u.id)
      : [];
  }

  // Id degli owner che hanno accesso perché assegnatari (status 'done') di un
  // incarico: vengono aggiunti agli owner automaticamente da handleAssigneeChange.
  // Rimuoverli da QUESTO dialog toglierebbe l'accesso lasciando però l'incarico
  // assegnato (denorm + filtri + export) → stato incoerente "assegnato ma senza
  // accesso". Vanno gestiti dalla relativa colonna incarichi. Qui li blocchiamo.
  // I dati arrivano dai CustomValues `${prefix}Assignees` iniettati da getLead.
  get lockedOwnerIds(): Set<number> {
    const { lead } = this;
    const ids = new Set<number>();
    if (!lead || !Array.isArray(lead.CustomValues)) return ids;

    const storeTypes = this.$store.getters.getAssignmentTypes as AssignmentType[];
    const prefixes = (Array.isArray(storeTypes) && storeTypes.length > 0)
      ? storeTypes.map((t) => t.id)
      : ['practice', 'project', 'enea', 'dogane', 'installer'];

    prefixes.forEach((prefix) => {
      const cv = lead.CustomValues.find((v) => v.name === `${prefix}Assignees`);
      if (!cv || !cv.value) return;
      let parsed: unknown;
      try { parsed = JSON.parse(cv.value); } catch { return; }
      if (!Array.isArray(parsed)) return;
      (parsed as Array<{ status?: string; assigneeId?: number | null }>).forEach((a) => {
        if (a && a.status === 'done' && a.assigneeId != null) ids.add(Number(a.assigneeId));
      });
    });

    return ids;
  }

  isLockedOwner(id: number): boolean {
    return this.lockedOwnerIds.has(id);
  }

  get noSelectedRequester() {
    return !this.update.requester;
  }

  get noSelectedOwners() {
    return !this.update.owners
      || (Array.isArray(this.update.owners) && this.update.owners.length === 0);
  }

  get form(): Vue & { validate: () => boolean } {
    return this.$refs.form as Vue & { validate: () => boolean };
  }

  hasPermission(codename: PermissionCode) {
    if (!this.user) return false;
    if (this.user.isSuperUser) return true;

    return this.userPermissions.includes(codename);
  }

  onCancel() {
    this.dialog = false;
  }

  async onUpdate() {
    if (this.form.validate()) {
      this.loading = true;

      await this.$store.dispatch('updateLead', {
        updateType: 'owners',
        update: this.update,
      });

      this.dialog = false;
      setTimeout(() => {
        this.loading = false;
      }, 500);
    }
  }

  removeOwner(owner: User) {
    console.log('EditLeadUsersDialog: removeOwner() owner =', owner); // eslint-disable-line no-console

    // Gli assegnatari di incarico non sono rimovibili da qui.
    if (this.isLockedOwner(owner.id)) return;

    const newOwners = this.update.owners.filter((ownerUserId) => ownerUserId !== owner.id);

    this.$set(this.update, 'owners', newOwners);
  }
}
